export type MissionHeatmapTooltipPlacement = "left" | "right" | "above" | "below";

export type MissionHeatmapTooltipPositionInput = Readonly<{
  anchorX: number;
  anchorY: number;
  containerWidth: number;
  containerHeight: number;
  tooltipWidth: number;
  tooltipHeight: number;
  cursorGap?: number;
  padding?: number;
}>;

export type MissionHeatmapTooltipPosition = Readonly<{
  left: number;
  top: number;
  placement: MissionHeatmapTooltipPlacement;
}>;

function clampHorizontalCenter(
  anchorX: number,
  tooltipWidth: number,
  containerWidth: number,
  padding: number,
): number {
  const halfWidth = tooltipWidth / 2;
  return Math.min(
    containerWidth - padding - halfWidth,
    Math.max(padding + halfWidth, anchorX),
  );
}

function clampVerticalAnchor(
  anchorY: number,
  tooltipHeight: number,
  containerHeight: number,
  padding: number,
): number {
  const halfHeight = tooltipHeight / 2;
  const minCenter = -tooltipHeight * 0.9 + halfHeight;
  const maxCenter = containerHeight - padding - halfHeight;
  return Math.min(maxCenter, Math.max(minCenter, anchorY));
}

function trySidePlacement(
  side: "left" | "right",
  input: MissionHeatmapTooltipPositionInput,
  cursorGap: number,
  padding: number,
): MissionHeatmapTooltipPosition | null {
  const left = side === "right"
    ? input.anchorX + cursorGap
    : input.anchorX - cursorGap - input.tooltipWidth;
  if (left + input.tooltipWidth > input.containerWidth - padding) {
    return null;
  }
  if (left < padding) {
    return null;
  }

  return {
    placement: side,
    left,
    top: clampVerticalAnchor(
      input.anchorY,
      input.tooltipHeight,
      input.containerHeight,
      padding,
    ),
  };
}

function tryAbovePlacement(
  input: MissionHeatmapTooltipPositionInput,
  cursorGap: number,
): MissionHeatmapTooltipPosition {
  return {
    placement: "above",
    left: clampHorizontalCenter(
      input.anchorX,
      input.tooltipWidth,
      input.containerWidth,
      input.padding ?? 8,
    ),
    top: input.anchorY - cursorGap - input.tooltipHeight,
  };
}

export function resolveMissionHeatmapTooltipPosition(
  input: MissionHeatmapTooltipPositionInput,
): MissionHeatmapTooltipPosition {
  const cursorGap = input.cursorGap ?? 12;
  const padding = input.padding ?? 8;
  const sideOrder: readonly ("left" | "right")[] = input.anchorX > input.containerWidth / 2
    ? ["left", "right"]
    : ["right", "left"];

  for (const side of sideOrder) {
    const candidate = trySidePlacement(side, input, cursorGap, padding);
    if (candidate) {
      return candidate;
    }
  }

  return tryAbovePlacement(input, cursorGap);
}
